// Version-skew detection for already-open SPA tabs.
//
// index.html is served with no-store, so a NAVIGATION always picks up a new
// deploy — but an open tab keeps running the old bundle forever (operator
// spent hours reporting bugs that were already fixed). The backend liveness
// probe (GET /api/v2/health/liveness, polled every ~15s by useNetworkStatus)
// now carries the image git SHA as `build`; this monitor memorizes the first
// build it sees and schedules a SAFE reload when the build changes.
//
// Safety policy (this app is a live trading robot):
// - NEVER reload while the robot is running or a trade is pending
//   (`isReloadBlocked` is injected — see app.tsx wiring).
// - Reload immediately only when idle AND the tab is visible; otherwise keep
//   the new build pending and fire on the next liveness report / visibility
//   change once idle.
// - At most ONE reload per build id (sessionStorage + in-memory guard) so a
//   misbehaving cache can never cause a reload loop.
// - "dev" builds (local backend without APP_BUILD_SHA) never trigger reloads.

/** Build id reported by local/dev backends — disables auto-reload entirely. */
export const DEV_BUILD_ID = "dev";

/** sessionStorage key recording the last build id this tab reloaded for. */
export const RELOADED_FOR_BUILD_KEY = "quartel:version-skew:reloaded-for";

export interface VersionSkewDeps {
  /** True while the robot is running or a trade is active — blocks reload. */
  readonly isReloadBlocked: () => boolean;
  /** True when the tab is visible; hidden tabs defer the reload. */
  readonly isTabVisible: () => boolean;
  /** Seam over window.location.reload() so tests inject a named fake. */
  readonly reloadPage: () => void;
  /** Seam over sessionStorage (anti-loop guard survives the reload). */
  readonly sessionStore: Pick<Storage, "getItem" | "setItem">;
}

export class VersionSkewMonitor {
  private baselineBuild: string | null = null;
  private pendingBuild: string | null = null;
  // In-memory mirror of the sessionStorage guard for environments where
  // storage throws (Safari private mode / quota exceeded).
  private reloadedForBuild: string | null = null;
  private readonly pendingListeners = new Set<() => void>();

  constructor(private readonly deps: VersionSkewDeps) {}

  /** Feed the `build` field of each liveness response (unknown-typed JSON). */
  reportLivenessBuild(build: unknown): void {
    const normalized = normalizeBuildId(build);
    if (normalized === null) return;
    if (this.baselineBuild === null) {
      this.baselineBuild = normalized;
      return;
    }
    if (normalized === this.baselineBuild) {
      // Rollback to the baseline build — any pending skew is gone.
      this.setPendingBuild(null);
      return;
    }
    this.setPendingBuild(normalized);
    this.evaluatePendingReload();
  }

  /** New build this tab has seen but NOT reloaded onto yet (null = current).
   *  Operador 05/07: the silent defer while the robot runs left him testing
   *  fixes on a stale bundle — the skew badge projects this state visibly. */
  getPendingBuild(): string | null {
    return this.pendingBuild;
  }

  /** Listen for pending-build transitions (useSyncExternalStore contract).
   *  Returns the unsubscribe. Example:
   *  `useSyncExternalStore((cb) => monitor.subscribePending(cb), () => monitor.getPendingBuild())` */
  subscribePending(listener: () => void): () => void {
    this.pendingListeners.add(listener);
    return (): void => {
      this.pendingListeners.delete(listener);
    };
  }

  private setPendingBuild(build: string | null): void {
    if (this.pendingBuild === build) return;
    this.pendingBuild = build;
    for (const listener of this.pendingListeners) listener();
  }

  /** Re-check a deferred reload (called on each poll + visibilitychange). */
  evaluatePendingReload(): void {
    const build = this.pendingBuild;
    if (build === null) return;
    if (this.hasAlreadyReloadedFor(build)) return;
    if (this.deps.isReloadBlocked()) return;
    if (!this.deps.isTabVisible()) return;
    this.markReloadedFor(build);
    this.deps.reloadPage();
  }

  private hasAlreadyReloadedFor(build: string): boolean {
    if (this.reloadedForBuild === build) return true;
    try {
      return this.deps.sessionStore.getItem(RELOADED_FOR_BUILD_KEY) === build;
    } catch {
      return false;
    }
  }

  private markReloadedFor(build: string): void {
    this.reloadedForBuild = build;
    try {
      this.deps.sessionStore.setItem(RELOADED_FOR_BUILD_KEY, build);
    } catch {
      // Storage unavailable — the in-memory guard above still prevents a
      // same-instance loop; a fresh instance only exists after the reload.
    }
  }
}

/** Returns a trimmed build id, or null for non-strings, "" and "dev". */
function normalizeBuildId(build: unknown): string | null {
  if (typeof build !== "string") return null;
  const trimmed = build.trim();
  if (trimmed === "" || trimmed === DEV_BUILD_ID) return null;
  return trimmed;
}
